import React, { useContext, useEffect, useRef, useState } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl }) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [reasonModalOpen, setReasonModalOpen] = useState(false);
	const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
	const [typeModalOpen, setTypeModalOpen] = useState(false);
	const [contactReasons, setContactReasons] = useState([]);
	const [contactTypes, setContactTypes] = useState([]);
	const [selectedReason, setSelectedReason] = useState(ticket.contactReasonId || "");
	const [selectedTypeId, setSelectedTypeId] = useState(ticket.contact?.contactTypeId || "");
	const [followUpDate, setFollowUpDate] = useState("");
	const [followUpNote, setFollowUpNote] = useState("");
	const [followUpNotifyAI, setFollowUpNotifyAI] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const { getSetting } = useContext(SettingsContext);

	useEffect(() => {
		return () => { isMounted.current = false; };
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = () => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleOpenReasonModal = async () => {
		try {
			const { data } = await api.get("/contact-reasons");
			setContactReasons(data);
			setSelectedReason(ticket.contactReasonId || "");
		} catch (_) {}
		setReasonModalOpen(true);
		handleClose();
	};

	const handleSaveReason = async () => {
		try {
			await api.put(`/tickets/${ticket.id}/reason`, { reasonId: selectedReason || null });
		} catch (err) { toastError(err); }
		setReasonModalOpen(false);
	};

	const handleOpenTypeModal = async () => {
		try {
			const { data } = await api.get("/contact-types");
			setContactTypes(data);
			setSelectedTypeId(ticket.contact?.contactTypeId || "");
		} catch (_) {}
		setTypeModalOpen(true);
		handleClose();
	};

	const handleSaveType = async () => {
		try {
			await api.put(`/contacts/phone/${ticket.contact.number}/type`, { contactTypeId: selectedTypeId || null });
		} catch (err) { toastError(err); }
		setTypeModalOpen(false);
	};

	const handleOpenFollowUpModal = () => {
		const now = new Date();
		now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
		setFollowUpDate(now.toISOString().slice(0, 16));
		setFollowUpNote("");
		setFollowUpNotifyAI(false);
		setFollowUpModalOpen(true);
		handleClose();
	};

	const handleSaveFollowUp = async () => {
		try {
			await api.post("/follow-ups", {
				ticketId: ticket.id,
				scheduledAt: followUpDate,
				note: followUpNote,
				notifyAI: followUpNotifyAI,
			});
		} catch (err) { toastError(err); }
		setFollowUpModalOpen(false);
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) setTransferTicketModalOpen(false);
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				keepMounted
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				open={menuOpen}
				onClose={handleClose}
			>
				<MenuItem onClick={handleOpenTransferModal}>
					{i18n.t("ticketOptionsMenu.transfer")}
				</MenuItem>
				<MenuItem onClick={handleOpenReasonModal}>
					{i18n.t("ticketOptionsMenu.assignReason")}
				</MenuItem>
				<MenuItem onClick={handleOpenTypeModal}>
					Asignar tipo de cliente
				</MenuItem>
				<MenuItem onClick={handleOpenFollowUpModal}>
					{i18n.t("ticketOptionsMenu.scheduleFollowUp")}
				</MenuItem>
				{(user.profile === "admin" || getSetting("userCanDeleteTicket") === "enabled") && (
					<MenuItem onClick={handleOpenConfirmationModal}>
						{i18n.t("ticketOptionsMenu.delete")}
					</MenuItem>
				)}
			</Menu>

			<ConfirmationModal
				title={i18n.t("ticketOptionsMenu.confirmationModal.title", { name: ticket.contact.name })}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticketWhatsappId={ticket.whatsappId}
			/>

			{/* Assign Contact Type Modal */}
			<Dialog open={typeModalOpen} onClose={() => setTypeModalOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Asignar tipo de cliente</DialogTitle>
				<DialogContent>
					<FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 8 }}>
						<InputLabel>Tipo</InputLabel>
						<Select
							value={selectedTypeId}
							onChange={e => setSelectedTypeId(e.target.value)}
							label="Tipo"
						>
							<MenuItem value=""><em>Sin tipo</em></MenuItem>
							{contactTypes.map(t => (
								<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					{contactTypes.length === 0 && (
						<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
							No hay tipos definidos. Configurá uno en Configuración.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setTypeModalOpen(false)}>Cancelar</Button>
					<Button onClick={handleSaveType} color="primary" variant="contained">Guardar</Button>
				</DialogActions>
			</Dialog>

			{/* Assign Reason Modal */}
			<Dialog open={reasonModalOpen} onClose={() => setReasonModalOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Asignar motivo de contacto</DialogTitle>
				<DialogContent>
					<FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 8 }}>
						<InputLabel>Motivo</InputLabel>
						<Select
							value={selectedReason}
							onChange={e => setSelectedReason(e.target.value)}
							label="Motivo"
						>
							<MenuItem value=""><em>Sin motivo</em></MenuItem>
							{contactReasons.map(r => (
								<MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					{contactReasons.length === 0 && (
						<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
							No hay motivos definidos. Configurá uno en Configuración.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setReasonModalOpen(false)}>Cancelar</Button>
					<Button onClick={handleSaveReason} color="primary" variant="contained">Guardar</Button>
				</DialogActions>
			</Dialog>

			{/* Follow-up Modal */}
			<Dialog open={followUpModalOpen} onClose={() => setFollowUpModalOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Programar seguimiento</DialogTitle>
				<DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 16 }}>
					<TextField
						label="Fecha y hora"
						type="datetime-local"
						value={followUpDate}
						onChange={e => setFollowUpDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth
						variant="outlined"
						size="small"
					/>
					<TextField
						label="Nota (opcional)"
						multiline
						rows={3}
						value={followUpNote}
						onChange={e => setFollowUpNote(e.target.value)}
						fullWidth
						variant="outlined"
						size="small"
					/>
					<FormControlLabel
						control={<Switch checked={followUpNotifyAI} onChange={e => setFollowUpNotifyAI(e.target.checked)} />}
						label="Notificar a IA al vencer"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setFollowUpModalOpen(false)}>Cancelar</Button>
					<Button onClick={handleSaveFollowUp} color="primary" variant="contained" disabled={!followUpDate}>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default TicketOptionsMenu;
